import { Colors, extendTheme, ThemeConfig } from "@chakra-ui/react";

// Color configuration for light/dark mode
const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const minimalist: Colors = {
  primary: {
    "50": "#d5d8db",
    "100": "#c4c8cc",
    "200": "#acb2b7",
    "300": "#949ca3",
    "400": "#7c868e",
    "500": "#6c757d",
    "600": "#5f676e",
    "700": "#4e545a",
    "800": "#3c4246",
    "900": "#2b2f32",
  },
  background: {
    "50": "#f6f7f8",
    "100": "#f2f3f5",
    "200": "#edeff1",
    "300": "#e7eaed",
    "400": "#e2e5e9",
    "500": "#dee2e6",
    "600": "#b4bdc6",
    "700": "#7c8c9c",
    "800": "#4e5b68",
    "900": "#242a30",
  },
  secondary: {
    "50": "#e4e4e4",
    "100": "#d8d8d8",
    "200": "#c9c9c9",
    "300": "#b9b9b9",
    "400": "#aaaaaa",
    "500": "#9e9e9e",
    "600": "#8b8b8b",
    "700": "#717171",
    "800": "#575757",
    "900": "#3d3d3d",
  },
  accent: {
    "50": "#c5dcf1",
    "100": "#adcdeb",
    "200": "#8cb9e4",
    "300": "#6ba5dc",
    "400": "#4a91d4",
    "500": "#3182ce",
    "600": "#2b72b5",
    "700": "#235e94",
    "800": "#1b4973",
    "900": "#143452",
  },
};

const artistStudio: Colors = {
  primary: {
    "50": "#dbd1f3",
    "100": "#ccbdee",
    "200": "#b8a3e7",
    "300": "#a488e1",
    "400": "#8f6eda",
    "500": "#805ad5",
    "600": "#6a3dce",
    "700": "#542cae",
    "800": "#422287",
    "900": "#2f1961",
  },
  background: {
    "50": "#f0edfe",
    "100": "#eae5fe",
    "200": "#e1dbfe",
    "300": "#d9d1fe",
    "400": "#d0c7fd",
    "500": "#cabffd",
    "600": "#917afb",
    "700": "#441ef8",
    "800": "#2406b1",
    "900": "#110355",
  },
  secondary: {
    "50": "#d5d8db",
    "100": "#c4c8cc",
    "200": "#acb2b7",
    "300": "#949ca3",
    "400": "#7c868e",
    "500": "#6c757d",
    "600": "#5f676e",
    "700": "#4e545a",
    "800": "#3c4246",
    "900": "#2b2f32",
  },
  accent: {
    "50": "#f4e4c4",
    "100": "#efd8ab",
    "200": "#e8c98a",
    "300": "#e1b969",
    "400": "#dbaa47",
    "500": "#d69e2e",
    "600": "#bf8c26",
    "700": "#9c731f",
    "800": "#7a5918",
    "900": "#574011",
  },
};

const dreamy: Colors = {
  primary: {
    "50": "#f3c9df",
    "100": "#eeb2d1",
    "200": "#e793bf",
    "300": "#e175ac",
    "400": "#da569a",
    "500": "#d53f8c",
    "600": "#c72c7c",
    "700": "#a32465",
    "800": "#7f1c4f",
    "900": "#5b1438",
  },
  background: {
    "50": "#fbebf3",
    "100": "#f9e2ed",
    "200": "#f7d6e6",
    "300": "#f4cadf",
    "400": "#f2bfd8",
    "500": "#f0b6d3",
    "600": "#e580b2",
    "700": "#d63887",
    "800": "#981f5c",
    "900": "#501130",
  },
  secondary: {
    "50": "#dbd1f3",
    "100": "#ccbdee",
    "200": "#b8a3e7",
    "300": "#a488e1",
    "400": "#8f6eda",
    "500": "#805ad5",
    "600": "#6a3dce",
    "700": "#542cae",
    "800": "#422287",
    "900": "#2f1961",
  },
  accent: {
    "50": "#c4edeb",
    "100": "#abe5e2",
    "200": "#89dad6",
    "300": "#68cfca",
    "400": "#46c5bf",
    "500": "#38b2ac",
    "600": "#319d97",
    "700": "#28807c",
    "800": "#1f6460",
    "900": "#164745",
  },
};

const clean: Colors = {
  primary: {
    "50": "#cae2f7",
    "100": "#b3d6f3",
    "200": "#95c6ee",
    "300": "#77b6e9",
    "400": "#59a5e5",
    "500": "#4299e1",
    "600": "#2489dc",
    "700": "#1d70b5",
    "800": "#16578d",
    "900": "#103e64",
  },
  background: {
    "50": "#ffffff",
    "100": "#ffffff",
    "200": "#ffffff",
    "300": "#ffffff",
    "400": "#ffffff",
    "500": "#ffffff",
    "600": "#d1d1d1",
    "700": "#949494",
    "800": "#575757",
    "900": "#1a1a1a",
  },
  secondary: {
    "50": "#d7dbe2",
    "100": "#c6ccd5",
    "200": "#afb8c4",
    "300": "#99a4b3",
    "400": "#828fa3",
    "500": "#718096",
    "600": "#627085",
    "700": "#505c6d",
    "800": "#3f4855",
    "900": "#2d333c",
  },
  accent: {
    "50": "#f7c5df",
    "100": "#f3acd1",
    "200": "#ef8bbf",
    "300": "#ea6aad",
    "400": "#e5499b",
    "500": "#e2308d",
    "600": "#d31e7d",
    "700": "#ad1866",
    "800": "#87134f",
    "900": "#600d39",
  },
};

const mindfulness: Colors = {
  primary: {
    "50": "#cae2f7",
    "100": "#b3d6f3",
    "200": "#95c6ee",
    "300": "#77b6e9",
    "400": "#59a5e5",
    "500": "#4299e1",
    "600": "#2489dc",
    "700": "#1d70b5",
    "800": "#16578d",
    "900": "#103e64",
  },
  background: {
    "50": "#f9fdff",
    "100": "#f7fcff",
    "200": "#f4fbff",
    "300": "#f1faff",
    "400": "#edf9ff",
    "500": "#ebf8ff",
    "600": "#96daff",
    "700": "#24b2ff",
    "800": "#0073b1",
    "900": "#00293f",
  },
  secondary: {
    "50": "#d7dbe2",
    "100": "#c6ccd5",
    "200": "#afb8c4",
    "300": "#99a4b3",
    "400": "#828fa3",
    "500": "#718096",
    "600": "#627085",
    "700": "#505c6d",
    "800": "#3f4855",
    "900": "#2d333c",
  },
  accent: {
    "50": "#fce8cf",
    "100": "#fbdebb",
    "200": "#fad1a0",
    "300": "#f9c485",
    "400": "#f7b769",
    "500": "#f6ad55",
    "600": "#f49a2d",
    "700": "#dd7e0c",
    "800": "#a86009",
    "900": "#724106",
  },
};

const vitality: Colors = {
  primary: {
    "50": "#faf0cd",
    "100": "#f7e9b7",
    "200": "#f4e19a",
    "300": "#f1d87d",
    "400": "#eecf61",
    "500": "#ecc94b",
    "600": "#e8bf29",
    "700": "#caa215",
    "800": "#9c7e10",
    "900": "#6e590c",
  },
  background: {
    "50": "#fffffb",
    "100": "#fffff9",
    "200": "#fffff7",
    "300": "#fffff4",
    "400": "#fffff2",
    "500": "#fffff0",
    "600": "#ffff99",
    "700": "#ffff25",
    "800": "#b0b000",
    "900": "#3c3c00",
  },
  secondary: {
    "50": "#d7dbe2",
    "100": "#c6ccd5",
    "200": "#afb8c4",
    "300": "#99a4b3",
    "400": "#828fa3",
    "500": "#718096",
    "600": "#627085",
    "700": "#505c6d",
    "800": "#3f4855",
    "900": "#2d333c",
  },
  accent: {
    "50": "#c5dcf1",
    "100": "#adcdeb",
    "200": "#8cb9e4",
    "300": "#6ba5dc",
    "400": "#4a91d4",
    "500": "#3182ce",
    "600": "#2b72b5",
    "700": "#235e94",
    "800": "#1b4973",
    "900": "#143452",
  },
};

const digitalClassroom: Colors = {
  primary: {
    "50": "#cae2f7",
    "100": "#b3d6f3",
    "200": "#95c6ee",
    "300": "#77b6e9",
    "400": "#59a5e5",
    "500": "#4299e1",
    "600": "#2489dc",
    "700": "#1d70b5",
    "800": "#16578d",
    "900": "#103e64",
  },
  background: {
    "50": "#f9fdff",
    "100": "#f7fcff",
    "200": "#f4fbff",
    "300": "#f1faff",
    "400": "#edf9ff",
    "500": "#ebf8ff",
    "600": "#96daff",
    "700": "#24b2ff",
    "800": "#0073b1",
    "900": "#00293f",
  },
  secondary: {
    "50": "#d7dbe2",
    "100": "#c6ccd5",
    "200": "#afb8c4",
    "300": "#99a4b3",
    "400": "#828fa3",
    "500": "#718096",
    "600": "#627085",
    "700": "#505c6d",
    "800": "#3f4855",
    "900": "#2d333c",
  },
  accent: {
    "50": "#fcd4d4",
    "100": "#fbc1c1",
    "200": "#f9a9a9",
    "300": "#f89090",
    "400": "#f67777",
    "500": "#f56565",
    "600": "#f23a3a",
    "700": "#e10f0f",
    "800": "#a80b0b",
    "900": "#6f0707",
  },
};

// Brand theme configurations
export const brandThemes = {
  minimalist,
  artistStudio,
  clean,
  dreamy,
  mindfulness,
  vitality,
  digitalClassroom,
};

const theme = extendTheme({
  config,
  colors: minimalist,
  components: {},
  styles: {},
});

export default theme;
